<template>
	<div
		class="checkbox"
		:disabled="disabled"
		:class="[disabled && 'is-disabled']"
		@click="clickHandler"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
	>
		<input
			ref="checkboxInput"
			class="checkbox__input"
			type="checkbox"
			name="checkbox"
			:id="uid"
			v-model="isChecked"
			@change="onChange"
			:aria-label="ariaLabel"
		/>
		<div class="checkbox__body" :style="checkboxStyle">
			<div class="checkbox__body-container">
				<Label
					class="checkbox__label"
					:forElementId="uid"
					v-if="labelText && !useSlot"
					:labelText="labelText"
				>
					<template v-slot:leading>
						<slot name="beforeLabel"> </slot>
					</template>

					<template v-slot:trailing>
						<slot name="afterLabel"> </slot>
					</template>
				</Label>
				<slot
					name="custom"
					v-bind:labelText="labelText"
					v-bind:checkboxValue="checkboxValue"
					v-if="useSlot"
				/>
				<slot
					name="subText"
					v-bind:option="{ subText: subText, value: checkboxValue }"
					v-if="subText"
				>
					<span
						class="checkbox__label-subtext"
						:ref="`subText${checkboxValue}`"
						v-html="subText"
					></span>
				</slot>
			</div>
			<HelpFeedback
				class="checkbox__help-feedback"
				:helpText="helpText"
				v-if="helpText"
			/>
		</div>
	</div>
</template>
<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import { parseBool } from '@/utils/parse-functions';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';

export default {
	name: 'Checkbox',
	mixins: [baseComponentMixin],
	components: {
		Label,
		HelpFeedback
	},
	data() {
		return {
			checked: parseBool(this.value)
		};
	},
	mounted() {
		this.updateIndeterminateState();
	},

	props: {
		labelText: {
			type: String,
			default: ''
		},
		value: {
			type: [Boolean, String],
			default: false
		},
		checkboxValue: {
			type: [String, Number, Boolean],
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		helpText: {
			type: String,
			default: null
		},
		width: {
			type: [String, Number],
			default: null
		},
		useSlot: {
			type: Boolean,
			default: false
		},
		subText: {
			type: String,
			default: null
		},
		indeterminate: {
			type: Boolean,
			default: false
		}
	},
	created() {
		this.checked = parseBool(this.value);
	},
	computed: {
		isChecked: {
			get: function() {
				return parseBool(this.value);
			},
			set: function(newValue) {
				this.checked = newValue;
			}
		},
		checkboxStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	},
	methods: {
		onChange() {
			this.$emit('input', this.checked);
			this.$emit('change', {
				value: this.checkboxValue,
				checked: this.checked
			});
		},
		clickHandler(e) {
			// Do not bubble up!
			e && e.stopPropagation();
		},
		onMouseEnter(e) {
			this.$emit('mouseenter', e);
		},
		onMouseLeave(e) {
			this.$emit('mouseleave', e);
		},
		setIndeterminate(newValue) {
			let checkbox = document.querySelector(`#${this.uid}`);
			if (checkbox) checkbox.indeterminate = newValue;
		},
		updateIndeterminateState() {
			if (this.$refs.checkboxInput) {
				this.$refs.checkboxInput.indeterminate = this.indeterminate;
			}
		}
	},
	watch: {
		indeterminate() {
			this.updateIndeterminateState();
		}
	}
};
</script>

<style lang="scss" scoped>
.checkbox {
	display: inline-flex;
	align-items: flex-start;
	user-select: none;
	color: var(--field-fg);
	position: relative;

	&__input {
		position: relative;
		cursor: pointer;
		-webkit-appearance: none;
		-moz-appearance: none;
		appearance: none;
		outline: none;
		vertical-align: middle;
		width: 16px;
		min-width: 16px;
		height: 16px;
		border-radius: $border-radius;
		border: $field-border;
		background-color: var(--field-bg);

		&:checked {
			&::after {
				@include transform-full-center;
				font-family: 'vue-icons';
				font-size: 10px;
				color: var(--field-fg);
				content: '\e980';
				height: 10px;
			}
		}

		&:indeterminate {
			&::after {
				@include transform-full-center;
				font-family: 'vue-icons';
				font-size: 10px;
				color: var(--field-fg);
				content: '\e9c7';
			}
		}

		&:hover {
			border: 2px solid var(--brand-2);
		}

		&:focus {
			&:before {
				position: absolute;
				content: '';
				@include transform-full-center;
				width: calc(100% + 8px);
				height: calc(100% + 8px);
				border-radius: $border-radius;
				border: $focus-width $focus-style var(--brand-2);
			}

			&:hover {
				&:before {
					width: calc(100% + 10px);
					height: calc(100% + 10px);
				}
			}
		}
	}

	&__body {
		margin-left: $gutter-md;
	}

	&__body-container {
		display: flex;
		align-items: flex-start;
		gap: $gutter-xs;
		flex-direction: column;
	}

	&__label {
		display: flex;
		font-size: $field-font-size;
		cursor: pointer;
		&::after {
			@include transform-full-center;
			content: '';
			height: 100%;
			width: 100%;
		}
	}

	&:deep(.label) {
		font-weight: $regular-font-weight;
	}
}
</style>
